import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Music, ChevronRight, Star, Clock, Heart, MessageCircle, Coins,
  Target, Zap, BarChart3, Trophy,
} from "lucide-react";
import { BadgesSection } from "@/components/BadgesSection";
import { FanLevelBadge } from "@/components/FanLevelBadge";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.4, ease: "easeOut" } }),
};

const ACTIVITY_ICONS = { like: Heart, comment: MessageCircle, bet: Coins, challenge: Target };
const ACTIVITY_COLORS = {
  like: "text-[#FF8FA3]",
  comment: "text-[#7000FF]",
  bet: "text-[#FF6B35]",
  challenge: "text-emerald-500",
};

const timeAgo = (dateStr) => {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}j`;
  return `${Math.floor(days / 30)}m`;
};

export const ProfileOverviewTab = ({ user, followedArtists, recentActivity }) => {
  const navigate = useNavigate();

  return (
    <motion.div
      key="overview"
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 10 }}
      className="space-y-5"
    >
      {user.artist_id && (
        <motion.button
          custom={0}
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          onClick={() => navigate(`/artist/${user.artist_id}`)}
          className="w-full bg-card border border-border rounded-3xl p-4 flex items-center justify-between hover:border-[#7000FF]/30 transition-all shadow-sm group"
          data-testid="my-artist-profile-btn"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#7000FF]/10 flex items-center justify-center group-hover:bg-[#7000FF]/20 transition-colors">
              <Music className="w-5 h-5 text-[#7000FF]" />
            </div>
            <div className="text-left">
              <p className="font-heading font-bold text-sm text-card-foreground">Mon profil artiste</p>
              <p className="text-[10px] text-muted-foreground">Gerer mes publications</p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-[#7000FF] group-hover:translate-x-1 transition-all" />
        </motion.button>
      )}

      {followedArtists.length > 0 && (
        <motion.div
          custom={1}
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          className="bg-card border border-border rounded-3xl p-5 shadow-sm"
          data-testid="followed-artists-section"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4 text-[#FF6B35]" />
              <h3 className="font-heading font-bold text-sm text-card-foreground">Artistes suivis</h3>
            </div>
            <button
              onClick={() => navigate("/discover")}
              className="text-[10px] text-[#FF6B35] hover:text-[#FF8FA3] transition-colors flex items-center gap-0.5"
            >
              Explorer <ChevronRight className="w-3 h-3" />
            </button>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
            {followedArtists.map((artist) => (
              <button
                key={artist.id}
                onClick={() => navigate(`/artist/${artist.id}`)}
                className="flex flex-col items-center gap-1.5 shrink-0 group"
                data-testid={`followed-artist-${artist.id}`}
              >
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FF6B35] to-[#7000FF] flex items-center justify-center overflow-hidden border-2 border-transparent group-hover:border-[#FF6B35] transition-all">
                  {artist.avatar_url ? (
                    <img src={artist.avatar_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-white font-heading font-bold text-lg">{artist.name[0].toUpperCase()}</span>
                  )}
                </div>
                <div className="text-center max-w-[70px]">
                  <p className="text-[10px] font-bold text-card-foreground truncate">{artist.name}</p>
                  {artist.verified && (
                    <svg className="w-3 h-3 text-[#7000FF] mx-auto" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                  )}
                </div>
              </button>
            ))}
          </div>
        </motion.div>
      )}

      {recentActivity.length > 0 && (
        <motion.div
          custom={2}
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          className="bg-card border border-border rounded-3xl p-5 shadow-sm"
          data-testid="recent-activity-section"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#7000FF]" />
              <h3 className="font-heading font-bold text-sm text-card-foreground">Activite recente</h3>
            </div>
            <button
              onClick={() => navigate("/stats")}
              className="text-[10px] text-[#FF6B35] hover:text-[#FF8FA3] transition-colors flex items-center gap-0.5"
            >
              Tout voir <ChevronRight className="w-3 h-3" />
            </button>
          </div>
          <div className="space-y-2">
            {recentActivity.slice(0, 5).map((act, i) => {
              const Icon = ACTIVITY_ICONS[act.type] || Zap;
              const color = ACTIVITY_COLORS[act.type] || "text-muted-foreground";
              return (
                <motion.div
                  key={act.id || `activity-${i}`}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="flex items-center gap-3 p-2.5 rounded-xl bg-white border border-border hover:border-[#FF6B35]/20 transition-colors"
                  data-testid={`activity-${i}`}
                >
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-muted">
                    <Icon className={`w-4 h-4 ${color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-card-foreground/80 truncate">{act.description}</p>
                    {act.content && (
                      <p className="text-[10px] text-muted-foreground truncate italic">"{act.content}"</p>
                    )}
                  </div>
                  <span className="text-[10px] text-muted-foreground shrink-0">{timeAgo(act.created_at)}</span>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      )}

      <motion.div custom={3} variants={fadeUp} initial="hidden" animate="visible">
        <FanLevelBadge />
      </motion.div>

      <motion.div custom={4} variants={fadeUp} initial="hidden" animate="visible">
        <BadgesSection />
      </motion.div>

      <motion.div custom={5} variants={fadeUp} initial="hidden" animate="visible" className="grid grid-cols-2 gap-3">
        <button
          onClick={() => navigate("/stats")}
          className="bg-card border border-border rounded-2xl p-4 flex flex-col items-center gap-2 hover:border-[#7000FF]/30 transition-all shadow-sm group"
          data-testid="stats-link-btn"
        >
          <div className="w-10 h-10 rounded-xl bg-[#7000FF]/10 flex items-center justify-center group-hover:bg-[#7000FF]/20 transition-colors">
            <BarChart3 className="w-5 h-5 text-[#7000FF]" />
          </div>
          <p className="font-heading font-bold text-xs text-card-foreground">Statistiques</p>
        </button>
        <button
          onClick={() => navigate("/leaderboard")}
          className="bg-card border border-border rounded-2xl p-4 flex flex-col items-center gap-2 hover:border-[#FF6B35]/30 transition-all shadow-sm group"
          data-testid="leaderboard-link-btn"
        >
          <div className="w-10 h-10 rounded-xl bg-[#FF6B35]/10 flex items-center justify-center group-hover:bg-[#FF6B35]/20 transition-colors">
            <Trophy className="w-5 h-5 text-[#FF6B35]" />
          </div>
          <p className="font-heading font-bold text-xs text-card-foreground">Classement</p>
        </button>
      </motion.div>
    </motion.div>
  );
};
